<template>
  <div class="pd-history">
    <!-- BANNER FOCO TEMPORAL -->
    <div v-if="showBanner" class="pds__banner">
      <span><i class="fa-solid fa-circle-info"></i> <strong>Foco Temporal Activo:</strong> Mostrando información de <strong>{{ activeYearsText }}</strong>.</span>
      <button @click="showBanner = false"><i class="fa-solid fa-xmark"></i></button>
    </div>

    <!-- SECCIÓN SUPERIOR: KPI y Buscador -->
    <div class="pdh__top-section">
      <div class="pdh__header">
        <div class="pdh__title-box">
          <h2>Explorador de Precios Unitarios e Históricos de Adquisición</h2>
          <p>Analiza tendencias de compra, variaciones de costo unitario en USD y cotizaciones de proveedores.</p>
        </div>
        <div class="pdh__select-wrap">
          <label>{{ textMaterial }} Seleccionado:</label>
          <DashboardSelect 
            v-model="localSku" 
            :options="skuList" 
            :placeholder="'Buscar ' + textMaterial.toLowerCase() + '...'" 
            :clearable="false"
            @change="updateSku" 
            class="pdh__custom-select" 
          />
        </div>
      </div>

      <div class="pdh__kpi-grid" v-if="localSku && matData.length > 0">
        <div class="pdh__kpi-card">
          <span>ÚLTIMO PRECIO USD</span>
          <h3>USD {{ formatN(lastPrice) }}</h3>
          <p>Última compra registrada</p>
        </div>
        <div class="pdh__kpi-card">
          <span>PRECIO MÍNIMO USD</span>
          <h3 class="pdh__text-green">USD {{ formatN(minPrice) }}</h3>
          <p>Mínimo en el período</p>
        </div>
        <div class="pdh__kpi-card">
          <span>PRECIO PROMEDIO USD</span>
          <h3>USD {{ formatN(avgPrice) }}</h3>
          <p>Promedio ponderado</p>
        </div>
        <div class="pdh__kpi-card">
          <span>PRECIO MÁXIMO USD</span>
          <h3 class="pdh__text-red">USD {{ formatN(maxPrice) }}</h3>
          <p>Máximo en el período</p>
        </div>
        <div class="pdh__kpi-card">
          <span>VARIACIÓN VS ANTERIOR</span>
          <div class="pdh__variance">
            <i :class="variance >= 0 ? 'fa-solid fa-circle-up pdh__text-red' : 'fa-solid fa-circle-down pdh__text-green'"></i>
            <h3 :class="variance >= 0 ? 'pdh__text-red' : 'pdh__text-green'">{{ formatVariance(variance) }}</h3>
          </div>
          <p>Vs. penúltima compra</p>
        </div>
        <div class="pdh__kpi-card pdh__kpi-card--highlight">
          <span v-if="!isService">CÓDIGO DE {{ textMaterial.toUpperCase() }}</span>
          <span v-else>{{ textMaterial.toUpperCase() }}</span>
          <h4 :title="materialName">{{ materialName }}</h4>
          <p class="pdh__sku-code" v-if="!isService">{{ localSku }}</p>
        </div>
      </div>
      <div v-else-if="!localSku" class="pdh__empty">
        Selecciona un {{ textMaterial.toLowerCase() }} en la lista desplegable para ver su histórico de compras.
      </div>
      <div v-else class="pdh__empty">
        No hay datos de compra para el {{ textMaterial.toLowerCase() }} seleccionado en este período.
      </div>
    </div>

    <!-- GRÁFICOS -->
    <div class="pdh__charts-grid" v-show="localSku && matData.length > 0">
      <div class="pds__chart-card pdh__chart-main">
        <div class="pds__card-head">
          <h3>HISTÓRICO DE PRECIO UNITARIO USD</h3>
          <p>Variación y fluctuaciones a lo largo del tiempo por fecha de emisión de orden</p>
        </div>
        <div class="pds__chart-wrap"><canvas ref="chartEvolucion"></canvas></div>
      </div>
      <div class="pds__chart-card">
        <div class="pds__card-head">
          <h3>PRECIO PROMEDIO USD POR PROVEEDOR</h3>
          <p>Comparativa de cotización entre distintos proveedores del mercado</p>
        </div>
        <div class="pds__chart-wrap"><canvas ref="chartProveedor"></canvas></div>
      </div>
    </div>

    <!-- GRÁFICO DE DESVIACIÓN -->
    <div class="pdh__charts-grid-full" v-show="localSku && matData.length > 0" style="margin-top: 24px;">
      <div class="pds__chart-card">
        <div class="pds__card-head" style="display:flex; justify-content:space-between; align-items:flex-start; flex-wrap:wrap; gap:12px;">
          <div>
            <h3>DESVIACIÓN DE PRECIO PROMEDIO USD</h3>
            <p>Evolución y variación porcentual del costo agrupado por período</p>
          </div>
          <div class="pdh__toggle-group" :class="{ 'pdh__toggle-group--dark': isDark }">
            <button :class="{active: devMode === 'month'}" @click="setDevMode('month')">Mensual</button>
            <button :class="{active: devMode === 'year'}" @click="setDevMode('year')">Anual</button>
          </div>
        </div>
        <div class="pds__chart-wrap"><canvas ref="chartDesviacion"></canvas></div>
      </div>
    </div>

    <!-- TABLA HISTÓRICA -->
    <DashboardTable
      v-show="localSku && matData.length > 0"
      style="margin-top: 24px;"
      :title="'Detalle Histórico de Compras del ' + textMaterial"
      subtitle="Historial transaccional exacto ordenado por fecha de emisión de orden de compra"
      :visibleItems="sortedTableData.length"
      :totalItems="sortedTableData.length"
      :search="tableSearch"
      :searchPlaceholder="'Buscar orden, proveedor o ' + textMaterial.toLowerCase() + '...'"
      @update:search="tableSearch = $event"
    >
      <template #header>
        <tr>
          <th>Fecha OC</th>
          <th>N° OC SUN EXPERT</th>
          <th>Proveedor</th>
          <th class="text-right">Cantidad</th>
          <th class="text-right">Precio Unitario $</th>
          <th class="text-right">Monto USD ($)</th>
        </tr>
      </template>
      <template #body>
        <tr v-for="(row, idx) in sortedTableData" :key="idx">
          <td>{{ row.OrdDate }}</td>
          <td><strong>{{ row.OrdDocumentNumber }}</strong></td>
          <td>{{ row.SupName }}</td>
          <td class="text-right">{{ formatInt(row.Quantity) }}</td>
          <td class="text-right"><strong>{{ formatN(row.UnitPrice) }}</strong></td>
          <td class="text-right pdh__text-green"><strong>${{ formatN2(row.TotalAmount) }}</strong></td>
        </tr>
      </template>
    </DashboardTable>
  </div>
</template>

<script>
import DashboardSelect from './DashboardSelect.vue';
import DashboardTable from './DashboardTable.vue';

export default {
  name: 'DashboardHistory',
  components: { DashboardSelect, DashboardTable },
  props: {
    dataset:     { type: Array,  required: true },
    selectedSku: { type: String, default: null },
    orderType:   { type: [String, Number], default: null },
    isDark:      { type: Boolean, default: false }
  },
  data() {
    return {
      localSku: this.selectedSku,
      charts: {},
      showBanner: true,
      tableSearch: '',
      devMode: 'month'
    };
  },
  computed: {
    isService() { return String(this.orderType) === '3'; },
    textMaterial() { return this.isService ? 'Servicio' : 'Material'; },
    
    activeYearsText() {
      if (!this.dataset || this.dataset.length === 0) return 'Ningún año disponible';
      const years = new Set(this.dataset.map(r => {
        if (!r.OrdDate) return null;
        const parts = r.OrdDate.split('/');
        if (parts.length === 3) return parts[2].split(' ')[0];
        return null;
      }).filter(y => y !== null));
      
      const sortedYears = Array.from(years).sort();
      if (sortedYears.length === 0) return 'Sin fechas registradas';
      if (sortedYears.length === 1) return sortedYears[0];
      if (sortedYears.length === 2) return `${sortedYears[0]} y ${sortedYears[1]}`;
      const last = sortedYears.pop();
      return `${sortedYears.join(', ')} y ${last}`;
    },
    skuList() {
      const map = new Map();
      this.dataset.forEach(r => {
        const key = this.isService ? (r.ItemDescription || 'Sin Descripción') : r.ItemCode;
        const label = this.isService ? (r.ItemDescription || 'Sin Descripción') : `${r.ItemCode} - ${r.ItemDescription || ''}`;
        
        if (key && !map.has(key)) {
          map.set(key, label);
        }
      });
      return Array.from(map.entries()).map(([id, name]) => ({ value: id, label: name })).sort((a,b) => a.label.localeCompare(b.label));
    },
    matData() {
      if (!this.localSku) return [];
      // Filtrar y ordenar de más antiguo a más reciente (para los gráficos)
      return this.dataset
        .filter(r => (this.isService ? r.ItemDescription : r.ItemCode) === this.localSku)
        .sort((a, b) => this.parseDate(a.OrdDate) - this.parseDate(b.OrdDate));
    },
    sortedTableData() {
      // Para la tabla, lo normal es ver de más reciente a más antiguo
      const data = [...this.matData].reverse();
      if (!this.tableSearch) return data;
      const term = this.tableSearch.toLowerCase();
      return data.filter(r => 
        (r.OrdDocumentNumber && String(r.OrdDocumentNumber).toLowerCase().includes(term)) ||
        (r.SupName && String(r.SupName).toLowerCase().includes(term))
      );
    },
    lastPurchase() { return this.matData.length > 0 ? this.matData[this.matData.length - 1] : null; },
    penultimatePurchase() { return this.matData.length > 1 ? this.matData[this.matData.length - 2] : null; },
    
    lastPrice() { return this.lastPurchase ? (this.lastPurchase.UnitPrice || 0) : 0; },
    minPrice()  { return this.matData.length > 0 ? Math.min(...this.matData.map(x => x.UnitPrice || 0)) : 0; },
    maxPrice()  { return this.matData.length > 0 ? Math.max(...this.matData.map(x => x.UnitPrice || 0)) : 0; },
    avgPrice()  { 
      if (this.matData.length === 0) return 0;
      return this.matData.reduce((acc, r) => acc + (r.UnitPrice || 0), 0) / this.matData.length;
    },
    variance() {
      if (!this.penultimatePurchase) return 0;
      const prev = this.penultimatePurchase.UnitPrice || 0;
      const curr = this.lastPrice;
      if (prev === 0) return 0;
      return ((curr - prev) / prev) * 100;
    },
    materialName() { return this.lastPurchase ? this.lastPurchase.ItemDescription : ''; }
  },
  watch: {
    dataset() {
      if (this.skuList.length > 0 && (!this.localSku || !this.skuList.find(s => s.value === this.localSku))) {
        this.localSku = this.skuList[0].value;
        this.updateSku();
      }
      this.$nextTick(() => { this.renderCharts(); });
    },
    localSku() {
      this.$nextTick(() => { this.renderCharts(); });
    },
    isDark() {
      this.$nextTick(() => { this.renderCharts(); });
    }
  },
  mounted() {
    if (!this.localSku && this.skuList.length > 0) {
      this.localSku = this.skuList[0].value;
      this.updateSku();
    }
    this.renderCharts();
  },
  beforeDestroy() {
    Object.values(this.charts).forEach(c => c.destroy());
  },
  methods: {
    updateSku() {
      this.$emit('update:selectedSku', this.localSku);
    },
    parseDate(dStr) {
      if (!dStr) return 0;
      const parts = dStr.split('/');
      if (parts.length >= 3) {
        return new Date(parts[2].split(' ')[0], parts[1] - 1, parts[0]).getTime();
      }
      return 0;
    },
    formatN(val) { return Number(val).toFixed(3); },
    formatN2(val) { return Number(val).toLocaleString(undefined, { minimumFractionDigits:2, maximumFractionDigits:2 }); },
    formatInt(val) { return Number(val).toLocaleString(); },
    formatVariance(val) { return (val > 0 ? '+' : '') + val.toFixed(2) + '%'; },
    
    setDevMode(mode) {
      this.devMode = mode;
      this.renderDesviacion();
    },
    getMonthName(m) {
      const ms = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
      return ms[parseInt(m,10)-1] || m;
    },
    renderCharts() {
      if (!this.localSku || this.matData.length === 0) return;
      if (!window.Chart) return;
      this.renderEvolucion();
      this.renderProveedor();
      this.renderDesviacion();
    },
    
    renderEvolucion() {
      if (this.charts.evolucion) this.charts.evolucion.destroy();
      const ctx = this.$refs.chartEvolucion;
      if (!ctx) return;
      
      const tc = this.isDark ? '#F8FAFC' : '#64748B';
      const gc = this.isDark ? '#334155' : 'rgba(200,200,200,0.15)';
      
      const labels = this.matData.map(r => r.OrdDate.substring(0,5)); // Solo DD/MM
      const data = this.matData.map(r => r.UnitPrice || 0);
      
      this.charts.evolucion = new window.Chart(ctx, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            data,
            borderColor: '#0A5C36',
            backgroundColor: 'rgba(10, 92, 54, 0.05)',
            borderWidth: 2.5,
            fill: true,
            tension: 0.1,
            pointRadius: 5,
            pointHoverRadius: 7,
            pointBackgroundColor: '#0A5C36',
            pointBorderColor: '#fff',
            pointBorderWidth: 1.5
          }]
        },
        options: {
          responsive: true, maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            x: { grid: { display: false }, ticks: { color: tc, font: { size: 9 } } },
            y: { grid: { color: gc }, ticks: { color: tc, font: { size: 9 } } }
          }
        }
      });
    },
    
    renderDesviacion() {
      if (this.charts.desviacion) this.charts.desviacion.destroy();
      const ctx = this.$refs.chartDesviacion;
      if (!ctx) return;
      
      const tc = this.isDark ? '#F8FAFC' : '#64748B';
      const gc = this.isDark ? '#334155' : 'rgba(200,200,200,0.15)';
      
      const groups = {};
      this.matData.forEach(r => {
         if(!r.OrdDate) return;
         const parts = r.OrdDate.split('/');
         if(parts.length !== 3) return;
         const y = parts[2].split(' ')[0];
         const m = parts[1];
         
         const key = this.devMode === 'year' ? y : `${y}-${m}`;
         const label = this.devMode === 'year' ? y : `${this.getMonthName(m)} ${y}`;
         const order = this.devMode === 'year' ? y : `${y}${m}`;
         
         if (!groups[key]) groups[key] = { sum: 0, count: 0, label, order };
         groups[key].sum += (r.UnitPrice || 0);
         groups[key].count++;
      });
      
      const sorted = Object.values(groups).sort((a,b) => a.order.localeCompare(b.order));
      
      let prevAvg = null;
      const dataPoints = sorted.map(g => {
          const avg = g.sum / g.count;
          let variance = 0;
          if (prevAvg !== null && prevAvg > 0) {
             variance = ((avg - prevAvg) / prevAvg) * 100;
          }
          prevAvg = avg;
          return { label: g.label, avg, variance };
      });
      
      const labels = dataPoints.map(d => d.label);
      const data = dataPoints.map(d => d.avg);
      
      const bgColors = dataPoints.map(d => {
         if (d.variance > 0) return '#EF4444'; 
         if (d.variance < 0) return '#10B981'; 
         return '#94A3B8'; 
      });

      const drawVariancePlugin = {
          id: 'drawVariance',
          afterDatasetsDraw(chart) {
              const { ctx } = chart;
              chart.data.datasets.forEach((dataset, i) => {
                  const meta = chart.getDatasetMeta(i);
                  meta.data.forEach((element, index) => {
                      const d = dataPoints[index];
                      if (index > 0 && d.variance !== 0) {
                          ctx.fillStyle = d.variance > 0 ? '#EF4444' : '#10B981';
                          ctx.font = '700 10px Inter, sans-serif';
                          ctx.textAlign = 'center';
                          ctx.textBaseline = 'bottom';
                          const text = (d.variance > 0 ? '+' : '') + d.variance.toFixed(1) + '%';
                          ctx.fillText(text, element.x, element.y - 12);
                      }
                  });
              });
          }
      };
      
      this.charts.desviacion = new window.Chart(ctx, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            data,
            borderColor: '#0A5C36',
            backgroundColor: 'rgba(10, 92, 54, 0.05)',
            borderWidth: 2.5,
            fill: true,
            tension: 0.3,
            pointRadius: 5,
            pointHoverRadius: 7,
            pointBackgroundColor: '#0A5C36',
            pointBorderColor: '#fff',
            pointBorderWidth: 1.5
          }]
        },
        options: {
          responsive: true, maintainAspectRatio: false,
          plugins: { 
             legend: { display: false },
             tooltip: {
                callbacks: {
                   label: function(context) {
                      const d = dataPoints[context.dataIndex];
                      let l = 'Promedio: $' + Number(d.avg).toFixed(3);
                      if(context.dataIndex > 0) l += ` (${d.variance>0?'+':''}${d.variance.toFixed(2)}%)`;
                      return l;
                   }
                }
             }
          },
          scales: {
            x: { grid: { display: false }, ticks: { color: tc, font: { size: 9 } } },
            y: { grid: { color: gc }, ticks: { color: tc, font: { size: 9 } } }
          }
        },
        plugins: [drawVariancePlugin]
      });
    },

    renderProveedor() {
      if (this.charts.proveedor) this.charts.proveedor.destroy();
      const ctx = this.$refs.chartProveedor;
      if (!ctx) return;
      
      const tc = this.isDark ? '#F8FAFC' : '#64748B';
      const gc = this.isDark ? '#334155' : 'rgba(200,200,200,0.15)';
      
      const provMap = {};
      this.matData.forEach(r => {
        const p = r.SupName || 'Desconocido';
        if(!provMap[p]) provMap[p] = { sum:0, count:0 };
        provMap[p].sum += (r.UnitPrice || 0);
        provMap[p].count++;
      });
      
      const labels = Object.keys(provMap);
      const data = labels.map(p => provMap[p].sum / provMap[p].count);
      
      this.charts.proveedor = new window.Chart(ctx, {
        type: 'bar',
        data: {
          labels,
          datasets: [{
            data,
            backgroundColor: '#0A5C36',
            borderRadius: 4,
            barPercentage: 0.6
          }]
        },
        options: {
          indexAxis: 'y',
          responsive: true, maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            x: { grid: { color: gc }, ticks: { color: tc, font: { size: 9 } } },
            y: { 
              grid: { display: false }, 
              ticks: { 
                color: tc, font: { size: 9 },
                callback: function(val) {
                  let label = this.getLabelForValue(val) || '';
                  if (label.length <= 20) return label;
                  const words = label.split(' ');
                  const lines = [];
                  let current = '';
                  for (let w of words) {
                    if ((current + w).length > 20) {
                      if (current) lines.push(current.trim());
                      current = w + ' ';
                    } else {
                      current += w + ' ';
                    }
                  }
                  if (current) lines.push(current.trim());
                  return lines;
                }
              } 
            }
          }
        }
      });
    }
  }
};
</script>

<style scoped>
.pd-history {
  display: flex !important;
  flex-direction: column !important;
  gap: 24px !important;
}

/* BANNER */
.pds__banner {
  display: flex !important; align-items: center !important; justify-content: space-between !important;
  background: #ECFDF5 !important; border: 1px solid #A7F3D0 !important; color: #065F46 !important;
  padding: 10px 16px !important; border-radius: 12px !important; font-size: 12px !important;
}
.pds__banner button { background: none !important; border: none !important; cursor: pointer !important; color: #059669 !important; font-size: 14px !important; }

/* TOP SECTION */
.pdh__top-section {
  background: var(--pd-bg, #fff) !important;
  border: 1px solid var(--pd-border, #E2E8F0) !important;
  border-radius: 16px !important;
  padding: 24px !important;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05) !important;
}

.pdh__header {
  display: flex !important;
  justify-content: space-between !important;
  align-items: flex-end !important;
  border-bottom: 1px solid var(--pd-border, #F1F5F9) !important;
  padding-bottom: 16px !important;
  margin-bottom: 20px !important;
  flex-wrap: wrap !important;
  gap: 16px !important;
}

.pdh__title-box h2 {
  font-size: 14px !important; font-weight: 700 !important; color: var(--pd-text-main, #1E293B) !important; text-transform: uppercase !important; margin: 0 0 4px 0 !important;
}
.pdh__title-box p {
  font-size: 11px !important; color: var(--pd-text-muted, #94A3B8) !important; margin: 0 !important;
}

.pdh__select-wrap {
  display: flex !important; align-items: center !important; gap: 8px !important;
}
.pdh__select-wrap label {
  font-size: 11px !important; font-weight: 700 !important; color: var(--pd-text-muted, #64748B) !important; text-transform: uppercase !important;
}
.pdh__custom-select {
  width: 320px !important;
  font-family: 'Inter', sans-serif !important;
}
@media (max-width: 768px) {
  .pdh__select-wrap {
    flex-direction: column !important;
    align-items: flex-start !important;
    width: 100% !important;
    gap: 6px !important;
  }
  .pdh__custom-select { 
    width: 100% !important; 
  }
}

.pdh__empty {
  text-align: center !important; color: var(--pd-text-muted, #94A3B8) !important; padding: 40px !important; font-size: 13px !important;
}

/* KPI GRID */
.pdh__kpi-grid {
  display: grid !important;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)) !important;
  gap: 16px !important;
}
.pdh__kpi-card {
  background: var(--pd-bg, #fff) !important;
  border: 1px solid var(--pd-border, #E2E8F0) !important;
  padding: 16px !important;
  border-radius: 12px !important;
  display: flex !important; flex-direction: column !important; justify-content: space-between !important;
}
.pdh__kpi-card--highlight {
  background: var(--pd-sidebar-bg, #FCF9EC) !important;
  border-color: var(--pd-border, #E6E4D9) !important;
}
.pdh__kpi-card span {
  font-size: 10px !important; font-weight: 700 !important; color: var(--pd-text-muted, #64748B) !important; letter-spacing: 0.5px !important;
}
.pdh__kpi-card h3 {
  font-size: 20px !important; font-weight: 800 !important; color: var(--pd-text-main, #1E293B) !important; margin: 6px 0 !important; font-family: monospace !important;
}
.pdh__kpi-card h4 {
  font-size: 12px !important; font-weight: 700 !important; color: var(--pd-green, #0A5C36) !important; margin: 6px 0 !important;
  white-space: nowrap !important; overflow: hidden !important; text-overflow: ellipsis !important;
}
.pdh__kpi-card p {
  font-size: 10px !important; color: var(--pd-text-muted, #94A3B8) !important; margin: 0 !important;
}
.pdh__sku-code {
  font-family: monospace !important; font-size: 10px !important; color: var(--pd-text-muted, #64748B) !important;
}

.pdh__variance { display: flex !important; align-items: center !important; gap: 6px !important; }
.pdh__text-green { color: #10B981 !important; }
.pdh__text-red { color: #EF4444 !important; }

/* TOGGLE BUTTONS */
.pdh__toggle-group {
  display: flex !important; background: var(--pd-border, #F1F5F9) !important; border-radius: 8px !important; padding: 4px !important;
}
.pdh__toggle-group button {
  background: transparent !important; border: none !important; padding: 6px 12px !important; font-size: 10px !important; font-weight: 700 !important; color: var(--pd-text-muted, #64748B) !important; border-radius: 6px !important; cursor: pointer !important; transition: 0.2s !important; text-transform: uppercase !important;
}
.pdh__toggle-group button.active {
  background: var(--pd-bg, #fff) !important; color: var(--pd-text-main, #1E293B) !important; box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important;
}

/* TOGGLE BUTTONS DARK MODE */
.pdh__toggle-group--dark {
  background: #1E293B !important; border: 1px solid #334155 !important;
}
.pdh__toggle-group--dark button {
  color: #64748B !important;
}
.pdh__toggle-group--dark button.active {
  background: #334155 !important; color: #F8FAFC !important; box-shadow: 0 1px 3px rgba(0,0,0,0.2) !important;
}

/* CHARTS GRID */
.pdh__charts-grid {
  display: grid !important; grid-template-columns: 2fr 1fr !important; gap: 24px !important; min-width: 0 !important;
}
.pdh__charts-grid-full {
  display: grid !important; grid-template-columns: 1fr !important; min-width: 0 !important;
}
@media (max-width: 1024px) {
  .pdh__charts-grid { grid-template-columns: 1fr !important; }
}

/* Reusing chart classes from DashboardSummary */
.pds__chart-card {
  background: var(--pd-bg, #fff) !important; border: 1px solid var(--pd-border, #E2E8F0) !important; border-radius: 16px !important; padding: 20px !important;
  display: flex !important; flex-direction: column !important; min-width: 0 !important; overflow: hidden !important;
}
.pds__card-head h3 { font-size: 12px !important; font-weight: 700 !important; color: var(--pd-text-main, #1E293B) !important; margin: 0 0 2px 0 !important; }
.pds__card-head p { font-size: 10px !important; color: var(--pd-text-muted, #94A3B8) !important; margin: 0 !important; }
.pds__chart-wrap { position: relative !important; width: 100% !important; height: 240px !important; margin-top: 16px !important; flex: 1 !important; min-width: 0 !important; }


.text-right { text-align: right !important; }
</style>
